import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { ComponentType } from "react";
import { createPortal } from "react-dom";
import { useT } from "@/i18n";
import { canvasMessages } from "@/i18n/messages/canvas";
import { commonMessages } from "@/i18n/messages/common";
import { cn } from "@/lib/cn";
import { clampPopupToViewport } from "@/lib/popup-position";
import { Icons } from "@/components/ui/icons";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  MenuItemContent,
  menuItemVariants,
  menuSurfaceVariants,
} from "@/components/ui/dropdown-menu";
import { useCanvasStore } from "@/stores/canvas-store";
import { useSelectionStore } from "@/stores/selection-store";
import { useFramesStore } from "@/stores/frames-store";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { canPinFrame, pinFrames } from "@/lib/frame-pinning";
import { isFramePinned } from "@/lib/wall-membership";
import { selectWallContext } from "@/stores/wall-feed-store";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import { setFrameHidden } from "@/lib/frame-visibility";
import {
  copyFrames,
  deleteFrames,
  expandFrames,
} from "@/lib/frame-menu-actions";
import { enterExplicitFocus } from "@/lib/focus-presentation";

interface MultiSelectionMenuItemProps {
  icon: ComponentType<{ className?: string }>;
  label: string;
  testId: string;
  onClick: () => void;
  destructive?: boolean;
}

function MultiSelectionMenuItem({
  icon: Icon,
  label,
  testId,
  onClick,
  destructive,
}: MultiSelectionMenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      className={cn(
        menuItemVariants({ density: "roomy" }),
        "w-full text-left transition-colors hover:bg-glass-fill-heavy focus:outline-none focus-visible:bg-glass-fill-heavy",
        destructive ? "text-danger-light" : "text-primary",
      )}
      onClick={onClick}
      data-testid={testId}
    >
      <MenuItemContent icon={<Icon />}>{label}</MenuItemContent>
    </button>
  );
}

function MultiSelectionMenuSeparator() {
  return (
    <div
      className="my-appkit-2xs h-px bg-glass-stroke-subtle"
      role="separator"
    />
  );
}

export function MultiSelectionContextMenu() {
  const t = useT(canvasMessages);
  const tCommon = useT(commonMessages);
  const menu = useCanvasStore((s) => s.multiSelectContextMenu);
  const frameIds = useSelectionStore((s) => s.multiSelectedFrameIds);
  const hasPinnableSelection = useFramesStore(
    (s) =>
      menu !== null &&
      frameIds.some((id) => {
        const frame = s.framesById[id];
        return (
          !!frame &&
          !isFramePinned(frame) &&
          canPinFrame(frame, selectWallContext(s))
        );
      }),
  );
  const close = useCanvasStore((s) => s.closeMultiSelectContextMenu);
  const clearMultiSelect = useCanvasStore((s) => s.clearMultiSelect);
  const desktopMode = useDesktopMode((s) => s.mode);
  const menuRef = useRef<HTMLDivElement>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // The menu opens at the cursor — near a viewport edge (e.g. after
  // "Select all in page" from the bottom-right map) it would render
  // half off-screen, so pull the anchor back while open.
  const [position, setPosition] = useState({
    x: menu?.x ?? 0,
    y: menu?.y ?? 0,
  });

  useLayoutEffect(() => {
    if (!menu) return;
    const anchor = { x: menu.x, y: menu.y };
    const el = menuRef.current;
    if (!el) {
      setPosition(anchor);
      return;
    }
    const rect = el.getBoundingClientRect();
    setPosition(clampPopupToViewport(menu.x, menu.y, rect.width, rect.height));
  }, [menu]);
  const showFocusWindows = desktopMode === "denk";

  useEffect(() => {
    if (!menu) return;
    function handlePointerDown(e: PointerEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        close();
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menu, close]);

  const handleExpand = useCallback(() => {
    close();
    expandFrames(frameIds);
  }, [close, frameIds]);

  const handleFocusWindows = useCallback(() => {
    close();
    enterExplicitFocus(frameIds);
  }, [close, frameIds]);

  const handleCopyAll = useCallback(() => {
    close();
    void copyFrames(frameIds);
  }, [close, frameIds]);

  const handlePinAll = useCallback(() => {
    close();
    pinFrames(frameIds);
    clearMultiSelect();
  }, [close, frameIds, clearMultiSelect]);

  const handleHideAll = useCallback(() => {
    close();
    // setFrameHidden updates local state AND persists metadata.hidden
    for (const id of frameIds) {
      setFrameHidden(id, true);
    }
    clearMultiSelect();
  }, [close, frameIds, clearMultiSelect]);

  const handleDeleteAll = useCallback(() => {
    setShowDeleteConfirm(false);
    close();
    void deleteFrames(frameIds);
    clearMultiSelect();
  }, [close, frameIds, clearMultiSelect]);

  if (!menu && !showDeleteConfirm) return null;
  if (frameIds.length < 2 && !showDeleteConfirm) return null;

  return (
    <>
      {menu &&
        createPortal(
          <div
            ref={menuRef}
            className={cn(
              surfaceRoleClassName("menu"),
              menuSurfaceVariants(),
              "fixed min-w-[200px]",
            )}
            style={{ left: position.x, top: position.y }}
            role="menu"
            data-interactive="true"
            data-testid="multi-selection-context-menu"
            {...surfaceRoleAttributes("menu", {
              nativeLevel: "root",
              nativeGroup: "menus",
            })}
          >
            <div className="px-appkit-sm py-appkit-2xs text-appkit-mini text-muted">
              {t("framesSelected", { count: frameIds.length })}
            </div>
            <MultiSelectionMenuSeparator />
            {showFocusWindows && (
              <>
                <MultiSelectionMenuItem
                  icon={Icons.Scan}
                  label={t("focusTheseWindows")}
                  testId="multi-ctx-focus-windows"
                  onClick={handleFocusWindows}
                />
                <MultiSelectionMenuSeparator />
              </>
            )}
            {!showFocusWindows && (
              <MultiSelectionMenuItem
                icon={Icons.Maximize}
                label={t("expand")}
                testId="multi-ctx-expand"
                onClick={handleExpand}
              />
            )}
            <MultiSelectionMenuItem
              icon={Icons.Copy}
              label={tCommon("copy")}
              testId="multi-ctx-copy"
              onClick={handleCopyAll}
            />
            {hasPinnableSelection && (
              <MultiSelectionMenuItem
                icon={Icons.Pin}
                label={t("pinToLeft")}
                testId="multi-ctx-pin"
                onClick={handlePinAll}
              />
            )}
            <MultiSelectionMenuSeparator />
            <MultiSelectionMenuItem
              icon={Icons.EyeOff}
              label={t("hide")}
              testId="multi-ctx-hide"
              onClick={handleHideAll}
            />
            <MultiSelectionMenuItem
              destructive
              icon={Icons.Trash2}
              label={tCommon("delete")}
              testId="multi-ctx-delete"
              onClick={() => {
                close();
                setShowDeleteConfirm(true);
              }}
            />
          </div>,
          document.body,
        )}

      {showDeleteConfirm && (
        <ConfirmDialog
          title={t("deleteFramesTitle")}
          description={t("deleteFramesDescription", {
            count: frameIds.length,
          })}
          confirmLabel={tCommon("delete")}
          variant="danger"
          icon={<Icons.Trash2 className="h-3.5 w-3.5 text-status-error" />}
          onConfirm={handleDeleteAll}
          onCancel={() => setShowDeleteConfirm(false)}
          data-testid="multi-delete-confirm"
        />
      )}
    </>
  );
}
